import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import ResourceCard from "./components/ResourceCard";
import SearchBar from "./components/SearchBar";
import TenderCard from "./components/TenderCard";
import AcademicCalendar from "./components/AcademicCalendar";
import FilterModal from "./components/FilterModal";
import PreviewModal from "./components/PreviewModal";
import TenderDetailsModal from "./components/TenderDetailsModal";

const Resources = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") || "documents"
  );
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [isTenderDetailsModalOpen, setIsTenderDetailsModalOpen] =
    useState(false);
  const [selectedTender, setSelectedTender] = useState(null);
  // Static data variables are now defined above

  // Update activeTab when search params change
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && tabs.some((t) => t.id === tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Fetch academic events from API
  useEffect(() => {
    const API_BASE = import.meta.env.VITE_API_BASE || (import.meta.env.PROD ? "https://karumotti.ac.ke" : "http://localhost:5000");
    const fetchAcademicEvents = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/academic-events`);
        if (response.ok) {
          const data = await response.json();
          setAcademicEvents(data);
        }
      } catch (error) {
        console.error('Error fetching academic events:', error);
        // Fallback to empty array if API fails
        setAcademicEvents([]);
      } finally {
        setCalendarLoading(false);
      }
    };

    fetchAcademicEvents();
  }, []);

  // Static data for resources - no API fetching needed
  const staticUploadedFiles = [
    // Documents
   {
      id: 1,
      title: "RPL Application Form",
      description: "Recognition of Prior Learning application form",
      category: "documents",
      filename: "RPL_Application_Form.pdf",
      mimetype: "application/pdf",
      size: 2048000,
      uploadedAt: "2025-11-17T00:00:00.000Z",
      type: "pdf"
    },
    {
      id: 2,
      title: "RPL Brochure",
      description: "Information brochure about Recognition of Prior Learning program",
      category: "brochure",
      filename: "RPLBrochure.jpeg",
      mimetype: "image/jpeg",
      size: 1048576,
      uploadedAt: "2025-11-20T00:00:00.000Z",
      type: "image"
    },
    
    {
      id: 7,
      title: "KARUMO SERVICE CHARTER",
      description: "CITIZENS' SERVICE DELIVERY CHARTER -PDF in English and Kiswahili",
      category: "service-charter",
      filename: "KARUMO_SERVICE_CHARTER.pdf",
      mimetype: "application/pdf",
      size: 2097152,
      uploadedAt: "2025-11-22T00:00:00.000Z",
      type: "pdf"
    },
    {
      id: 8,
      title: "Employment Application Form",
      description: "Application form for employment opportunities at KTTI",
      category: "documents",
      filename: "Employment_Application_Form.pdf",
      mimetype: "application/pdf",
      size: 1024000,
      uploadedAt: "2024-01-25T00:00:00.000Z",
      type: "pdf"
    },
    
    {
      id: 12,
      title: "Admin TC Tender Notice",
      description: "Tender notice for administrative and teaching complex",
      category: "tenders",
      filename: "ADMIN_TC_TENDER_NOTICE.pdf",
      mimetype: "application/pdf",
      size: 2097152,
      uploadedAt: "2024-01-29T00:00:00.000Z",
      type: "pdf"
    },
    {
      id: 13,
      title: "Karumo TTI Admin Tuition Block Tender Document",
      description: "Complete tender documentation for admin tuition block phase 1",
      category: "tenders",
      filename: "Karumo_TTI_Admin_Tuition_Block_Phase_1_Tender_Doc_.pdf",
      mimetype: "application/pdf",
      size: 5242880,
      uploadedAt: "2024-01-30T00:00:00.000Z",
      type: "pdf"
    },
    {
      id: 14,
      title: "New Brochure October Edition",
      description: "Updated institutional brochure with latest information",
      category: "brochure",
      filename: "new bronchure oct edition-1.pdf",
      mimetype: "application/pdf",
      size: 3145728,
      uploadedAt: "2025-11-20T00:00:00.000Z",
      type: "pdf"
    }
  ];

  const staticTenders = [
   // {
    //   id: 1,
    //   title: "Supply and Installation of Laboratory Equipment",
    //   description: "Tender for supply and installation of modern laboratory equipment for science and engineering departments",
    //   category: "Procurement",
    //   openingDate: "2024-02-01",
    //   closingDate: "2024-02-28",
    //   status: "Closed",
    //   tenderNumber: "KTTI/T/2024/001",
    //   documents: [
    //     {
    //       name: "Tender Document",
    //       url: "/assets/files/ADMIN_TC_TENDER_NOTICE.pdf"
    //     }
    //   ],
    //   requirements: [
    //     "Valid business registration",
    //     "Tax compliance certificate",
    //     "Relevant experience in equipment supply",
    //     "Technical specifications compliance"
    //   ],
    //   contactEmail: "procurement@karumotti.ac.ke",
    //   contactPhone: "+254(0)705636443"
    // },
  ];

  const staticCareers = [
    {
      id: 1,
      title: "JOB ADVERT",
      description: "We are seeking a qualified personel to join our team for various positions.",
      location: "Karumo, Kenya",
      type: "Full-time",
      department: "Engineering",
      postedDate: "2026-08-10",
      applicationDeadline: "2026-09-11",
      salary: "Competitive",
      requirements: [
        ""
      ],
      contactEmail: "",
      filename: "September2026JobAdvert.pdf",
    },
  ];

  const staticGallery = [
    // Images
    {
      id: 1,
      title: "Carpentry Workshop",
      description: "Students engaged in practical carpentry training",
      category: "Workshops",
      type: "image",
      filename: "carpentry.jpg",
      mimetype: "image/jpeg",
      uploadedAt: "2024-01-10T00:00:00.000Z"
    },
    {
      id: 2,
      title: "Machinery and Equipment",
      description: "Modern machinery used in engineering training",
      category: "Facilities",
      type: "image",
      filename: "machinery.jpg",
      mimetype: "image/jpeg",
      uploadedAt: "2024-01-12T00:00:00.000Z"
    },
    {
      id: 3,
      title: "Students in Discussion",
      description: "Students participating in discussions learning",
      category: "Academic",
      type: "image",
      filename: "students.jpg",
      mimetype: "image/jpeg",
      uploadedAt: "2024-01-14T00:00:00.000Z"
    },
    {
      id: 4,
      title: "Dedication of new Tution Block",
      description: "Principal dedicating the new tuition block",
      category: "Leadership",
      type: "image",
      filename: "principal.jpg",
      mimetype: "image/jpeg",
      uploadedAt: "2024-01-16T00:00:00.000Z"
    },
    {
      id: 5,
      title: "Scout Activities",
      description: "Students participating in scouting activities",
      category: "Co-curricular",
      type: "image",
      filename: "scouts.jpg",
      mimetype: "image/jpeg",
      uploadedAt: "2024-01-22T00:00:00.000Z"
    },
    {
      id: 6,
      title: "Scout Training",
      description: "Scouting training and development activities",
      category: "Co-curricular",
      type: "image",
      filename: "scout2.jpg",
      mimetype: "image/jpeg",
      uploadedAt: "2024-01-24T00:00:00.000Z"
    },
    // Videos
    {
      id: 13,
      title: "Service Charter Video - English",
      description: "English video explaining our service charter and commitments",
      category: "Service Charter",
      type: "video",
      filename: "Video_English.mp4",
      mimetype: "video/mp4",
      uploadedAt: "2024-01-22T00:00:00.000Z"
    },
    {
      id: 14,
      title: "Service Charter Video - Swahili",
      description: "Kiswahili video ikielezea mkataba wetu wa huduma na ahadi",
      category: "Service Charter",
      type: "video",
      filename: "Video_Swahili.mp4",
      mimetype: "video/mp4",
      uploadedAt: "2024-01-22T00:00:00.000Z"
    },  
  
  ];

  // Mock data for resources (keeping for now, will be replaced with API later)
  const resources = [
    {
      id: 5,
      title: "CDACC Examination Schedule November/December 2025",
      description:
        "Complete examination timetable for all departments and courses for November/December 2025 Serries",
      type: "doc",
      category: "Academic",
      size: 512000,
      updatedAt: "2025-11-18",
      isNew: true,
      isBookmarked: true,
      fileUrl: "/assets/brochure.pdf",
    },
    {
      id: 6,
      title: "KNEC Examination Schedule November/December 2025",
      description:
        "Complete examination timetable for all departments and courses",
      type: "pdf",
      category: "Academic",
      size: 1536000,
      updatedAt: "2025-11-18",
      isNew: true,
      isBookmarked: false,
      fileUrl: "/assets/brochure.pdf",
    },
    {
      id: 1,
      title: "NOV/DEC 2025 CDACC External POE Verification",
      description: "External Verification of CDACC POE ",
      type: "pdf",
      category: "Forms",
      size: 2048000,
      updatedAt: "2025-10-28",
      isNew: true,
      isBookmarked: false,
      fileUrl: "/assets/files/brochure.pdf",
    },
    {
      id: 7,
      title: "Service Charter Video - English Version",
      description:
        "English video explaining our service charter and commitments",
      type: "mp4",
      category: "Service Charter",
      size: 52428800,
      updatedAt: "2024-01-22",
      isNew: true,
      isBookmarked: false,
      fileUrl: "/assets/files/Video_English.mp4",
    },
    {
      id: 8,
      title: "Service Charter Video - Swahili Version",
      description:
        "Kiswahili video ikielezea mkataba wetu wa huduma na ahadi",
      type: "mp4",
      category: "Service Charter",
      size: 78643200,
      updatedAt: "2024-01-22",
      isNew: true,
      isBookmarked: false,
      fileUrl: "/assets/files/Video_Swahili.mp4",
    },
    {
      id: 9,
      title: "Service Charter Audio - English  Version",
      description: "Audio narration of the service charter introduction",
      type: "mp3",
      category: "Service Charter",
      size: 15728640,
      updatedAt: "2024-01-22",
      isNew: true,
      isBookmarked: false,
      fileUrl: "/assets/files/Charter_English.mpeg",
    },
    {
      id: 10,
      title: "Service Charter Audio - Kwa Kiswahili",
      description:
        "Maelezo ya sauti kuhusu viwango vya huduma na hatua za ubora",
      type: "mp3",
      category: "Service Charter",
      size: 20971520,
      updatedAt: "2024-01-22",
      isNew: true,
      isBookmarked: false,
      fileUrl: "/assets/files/Charter_Kiswahili.mpeg",
    },
    {
      id: 11,
      title: "Service Charter Document",
      description:
        "CITIZENS' SERVICE DELIVERY CHARTER -PDF in English and Kiswahili",
      type: "pdf",
      category: "Service Charter",
      size: 2097152,
      updatedAt: "2024-01-22",
      isNew: true,
      isBookmarked: false,
      fileUrl: "/assets/files/KARUMO_SERVICE_CHARTER.pdf",
    },
  ];

  // Static data is now used instead of mock data

  // State for academic calendar
  const [academicEvents, setAcademicEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(true);

  // Use static data instead of state
  const uploadedFiles = staticUploadedFiles;
  const tenders = staticTenders;
  const careers = staticCareers;
  const gallery = staticGallery;

  // Mock data for categories
  const categories = [
    { id: "all", name: "All Resources", icon: "FileText" },
    { id: "policies", name: "Policies & Rules", icon: "Shield" },
    { id: "academic", name: "Academic Documents", icon: "BookOpen" },
  ];

  // Calculate resource counts by category
  const getResourceCounts = () => {
    const counts = { all: resources?.length };
    categories?.forEach((category) => {
      if (category?.id !== "all") {
        if (category?.id === "service-charter") {
          counts[category.id] = resources?.filter(
            (resource) =>
              resource?.category?.toLowerCase() === "service charter"
          )?.length;
        } else {
          counts[category.id] = resources?.filter(
            (resource) =>
              resource?.category?.toLowerCase() ===
              category?.name?.split(" ")?.[0]?.toLowerCase()
          )?.length;
        }
      }
    });
    return counts;
  };

  // Filter resources based on selected category and search query
  const getFilteredResources = () => {
    let filtered = resources;

    if (selectedCategory !== "all") {
      if (selectedCategory === "service-charter") {
        filtered = filtered?.filter(
          (resource) => resource?.category?.toLowerCase() === "service charter"
        );
      } else {
        const categoryName = categories
          ?.find((cat) => cat?.id === selectedCategory)
          ?.name?.split(" ")?.[0]
          ?.toLowerCase();
        filtered = filtered?.filter(
          (resource) => resource?.category?.toLowerCase() === categoryName
        );
      }
    }

    if (searchQuery) {
      filtered = filtered?.filter(
        (resource) =>
          resource?.title
            ?.toLowerCase()
            ?.includes(searchQuery?.toLowerCase()) ||
          resource?.description
            ?.toLowerCase()
            ?.includes(searchQuery?.toLowerCase())
      );
    }

    return filtered;
  };

  const handleDownload = (resource) => {
    if (resource?.fileUrl) {
      // For policies, open in new tab/window to obfuscate URL
      if (resource.category?.toLowerCase() === 'policies') {
        window.open(resource.fileUrl, '_blank', 'noopener,noreferrer');
      } else {
        // For all other documents, force download without opening
        const link = document.createElement("a");
        link.href = resource.fileUrl;
        link.download = resource.title + "." + resource.type;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      console.log("Downloading:", resource?.title);
    } else {
      console.error("No file URL available for download");
    }
  };

  const handlePreview = (resource) => {
    setSelectedResource(resource);
    setIsPreviewModalOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewModalOpen(false);
    setSelectedResource(null);
  };

  const handleBookmark = (resourceId) => {
    console.log("Toggling bookmark for resource:", resourceId);
    // Mock bookmark functionality
  };

  const handleTenderDetails = (tender) => {
    setSelectedTender(tender);
    setIsTenderDetailsModalOpen(true);
  };

  const handleCloseTenderDetails = () => {
    setIsTenderDetailsModalOpen(false);
    setSelectedTender(null);
  };

  const getActiveFilterCount = () => {
    return Object.values(appliedFilters)?.reduce(
      (count, arr) => count + (arr?.length || 0),
      0
    );
  };

  const tabs = [
    { id: "documents", name: "Documents", icon: "FileText" },
    { id: "service-charter", name: "Service Charter", icon: "FileCheck" },
    { id: "gallery", name: "Gallery", icon: "GalleryThumbnails" },
    { id: "tenders", name: "Open Tenders", icon: "Briefcase" },
    { id: "careers", name: "Careers", icon: "Briefcase" },
    { id: "calendar", name: "Academic Calendar", icon: "Calendar" },
  ];

  return (
    <>
      <Helmet>
        <title>KARUMO TECHNICAL TRAINING INSTITUTE</title>
        <meta
          name="description"
          content="Access comprehensive resources including application forms, academic policies, tender documents, and academic calendar at KTTI Digital Campus."
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero Section */}
        <section className="pt-20 pb-12 bg-gradient-to-tr from-blue-600 to-yellow-400">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center mb-6">
                <br></br>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-blue-600 mb-4">
                Resource Center
              </h1>
              <p className="text-xl text-white max-w-full mx-auto">
                Your comprehensive hub for documents, forms, policies, and
                academic resources. Everything you need for your educational
                journey at KTTI.
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12 bg-blue-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {/* Documents Tab */}
            {activeTab === "documents" && (
              <div className="grid lg:grid-cols-2 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-3">
                  <div className="mb-6">
                    <SearchBar
                      onSearch={setSearchQuery}
                      onFilterToggle={() => setIsFilterModalOpen(true)}
                      activeFilters={getActiveFilterCount()}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-6">
                    {(uploadedFiles?.filter(file => file.category === 'documents' || file.category === 'academic' || file.category === 'policies' || file.category === 'general' || file.category === 'fee-structure' || file.category === 'brochure' || file.category === 'rules-regulations') || getFilteredResources())?.map((resource) => {
                      // Determine file type from mimetype or filename
                      let fileType = resource.type;
                      if (!fileType && resource.mimetype) {
                        if (resource.mimetype === 'application/pdf') fileType = 'pdf';
                        else if (resource.mimetype.startsWith('video/')) fileType = 'mp4';
                        else if (resource.mimetype.startsWith('audio/')) fileType = 'mp3';
                        else if (resource.mimetype.startsWith('image/')) fileType = 'image';
                        else if (resource.mimetype === 'application/msword' || resource.mimetype === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') fileType = 'doc';
                        else if (resource.mimetype === 'application/vnd.ms-excel' || resource.mimetype === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') fileType = 'xls';
                        else if (resource.mimetype === 'application/vnd.ms-powerpoint' || resource.mimetype === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') fileType = 'ppt';
                        else if (resource.mimetype === 'application/zip') fileType = 'zip';
                      }
                      if (!fileType && resource.filename) {
                        const ext = resource.filename.split('.').pop()?.toLowerCase();
                        if (ext === 'pdf') fileType = 'pdf';
                        else if (['mp4', 'avi', 'mov'].includes(ext)) fileType = 'mp4';
                        else if (['mp3', 'wav', 'ogg'].includes(ext)) fileType = 'mp3';
                        else if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) fileType = 'image';
                        else if (['doc', 'docx'].includes(ext)) fileType = 'doc';
                        else if (['xls', 'xlsx'].includes(ext)) fileType = 'xls';
                        else if (['ppt', 'pptx'].includes(ext)) fileType = 'ppt';
                        else if (ext === 'zip') fileType = 'zip';
                      }

                      return (
                        <ResourceCard
                          key={resource?.id}
                          resource={{
                            ...resource,
                            type: fileType || resource.type,
                            fileUrl: resource.filename ? `/assets/files/${resource.filename}` : resource.fileUrl,
                            size: resource.size ? resource.size / 1024 / 1024 : resource.size,
                            updatedAt: resource.uploadedAt ? new Date(resource.uploadedAt).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) : resource.updatedAt,
                            isNew: resource.uploadedAt ? new Date(resource.uploadedAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) : resource.isNew,
                            isBookmarked: resource.isBookmarked || false
                          }}
                          onDownload={handleDownload}
                          onPreview={handlePreview}
                          onBookmark={handleBookmark}
                        />
                      );
                    })}
                  </div>

                  {getFilteredResources()?.length === 0 && (
                    <div className="text-center py-12">
                      <Icon
                        name="Search"
                        size={64}
                        className="mx-auto mb-4 text-black opacity-50"
                      />
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        No resources found
                      </h3>
                      <p className="text-black">
                        Try adjusting your search terms or filters to find what
                        you're looking for.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Service Charter Tab */}
            {activeTab === "service-charter" && (
              <div className="space-y-8">
                {/* Header */}
                <div className="text-center">
                  {/* <div className="flex items-center justify-center mb-6">
                  </div> */}
                  <h2 className="text-3xl font-bold text-foreground mb-4">
                    Service Charter
                  </h2>
                  <p className="text-xl text-muted-black max-w-fit mx-auto">
                    Our commitment to providing excellent service. Access our
                    service charter documents, watch introductory videos, and
                    listen to audio explanations of our standards and
                    procedures.
                  </p>
                </div>

                {/* Multimedia Content */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Videos Section */}
                  <div className="space-y-6">
                    <h3 className="text-2xl font-semibold text-foreground flex items-center space-x-2">
                      <Icon name="Video" size={24} />
                      <span>Service Charter Videos</span>
                    </h3>
                    {resources
                      ?.filter(
                        (resource) =>
                          resource?.category === "Service Charter" &&
                          resource?.type === "mp4"
                      )
                      ?.map((video) => (
                        <div
                          key={video?.id}
                          className="bg-card rounded-lg border border-border overflow-hidden"
                        >
                          <div className="p-4 border-b border-border">
                            <h4 className="font-semibold text-foreground">
                              {video?.title}
                            </h4>
                            <p className="text-sm text-black mt-1">
                              {video?.description}
                            </p>
                          </div>
                          <div className="aspect-video">
                            <video
                              src={video?.fileUrl}
                              className="w-full h-full border-0"
                              title={video?.title}
                              controls
                              preload="metadata"
                              //  poster="/assets/images/students.jpg"
                            />
                          </div>
                        </div>
                      ))}
                  </div>

                  {/* Audio Section */}
                  <div className="space-y-6">
                    <h3 className="text-2xl font-semibold text-foreground flex items-center space-x-2">
                      <Icon name="Music" size={24} />
                      <span>Service Charter Audio</span>
                    </h3>
                    {resources
                      ?.filter(
                        (resource) =>
                          resource?.category === "Service Charter" &&
                          resource?.type === "mp3"
                      )
                      ?.map((audio) => (
                        <div
                          key={audio?.id}
                          className="bg-card rounded-lg border border-border overflow-hidden"
                        >
                          <div className="p-4 border-b border-border">
                            <h4 className="font-semibold text-foreground">
                              {audio?.title}
                            </h4>
                            <p className="text-sm text-black mt-1">
                              {audio?.description}
                            </p>
                          </div>
                          <div className="p-4">
                            <audio
                              src={audio?.fileUrl}
                              controls
                              className="w-full"
                              title={audio?.title}
                              preload="metadata"
                            >
                              Your browser does not support the audio element.
                            </audio>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Documents Section */}
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-foreground flex items-center space-x-2">
                    <Icon name="FileText" size={24} />
                    <span>Service Charter Documents</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {(uploadedFiles?.filter(file => file.category === 'service-charter') || resources?.filter(resource => resource?.category === "Service Charter" && resource?.type === "pdf"))?.map((document) => {
                      // Determine file type from mimetype or filename
                      let fileType = document.type;
                      if (!fileType && document.mimetype) {
                        if (document.mimetype === 'application/pdf') fileType = 'pdf';
                        else if (document.mimetype.startsWith('video/')) fileType = 'mp4';
                        else if (document.mimetype.startsWith('audio/')) fileType = 'mp3';
                        else if (document.mimetype.startsWith('image/')) fileType = 'image';
                        else if (document.mimetype === 'application/msword' || document.mimetype === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') fileType = 'doc';
                        else if (document.mimetype === 'application/vnd.ms-excel' || document.mimetype === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') fileType = 'xls';
                        else if (document.mimetype === 'application/vnd.ms-powerpoint' || document.mimetype === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') fileType = 'ppt';
                        else if (document.mimetype === 'application/zip') fileType = 'zip';
                      }
                      if (!fileType && document.filename) {
                        const ext = document.filename.split('.').pop()?.toLowerCase();
                        if (ext === 'pdf') fileType = 'pdf';
                        else if (['mp4', 'avi', 'mov'].includes(ext)) fileType = 'mp4';
                        else if (['mp3', 'wav', 'ogg'].includes(ext)) fileType = 'mp3';
                        else if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) fileType = 'image';
                        else if (['doc', 'docx'].includes(ext)) fileType = 'doc';
                        else if (['xls', 'xlsx'].includes(ext)) fileType = 'xls';
                        else if (['ppt', 'pptx'].includes(ext)) fileType = 'ppt';
                        else if (ext === 'zip') fileType = 'zip';
                      }

                      return (
                        <ResourceCard
                          key={document?.id}
                          resource={{
                            ...document,
                            type: fileType || document.type,
                            fileUrl: document.filename ? `/assets/files/${document.filename}` : document.fileUrl,
                            size: document.size ? document.size / 1024 / 1024 : document.size,
                            updatedAt: document.uploadedAt ? new Date(document.uploadedAt).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) : document.updatedAt,
                            isNew: document.uploadedAt ? new Date(document.uploadedAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) : document.isNew,
                            isBookmarked: document.isBookmarked || false
                          }}
                          onDownload={handleDownload}
                          onPreview={handlePreview}
                          onBookmark={handleBookmark}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Gallery Tab */}
            {activeTab === "gallery" && (
              <div className="space-y-8">
                {/* Header */}
                <div className="text-center">
                  <h2 className="text-3xl font-bold text-foreground mb-4">
                    Institution Gallery
                  </h2>
                </div>

                {/* Images Section */}
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-foreground flex items-center space-x-2">
                    <Icon name="Image" size={24} />
                    <span>Images</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {gallery?.filter(item => item?.type === 'image')?.map((image) => (
                      <div key={image?.id} className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-lg transition-shadow duration-200">
                        <div className="relative overflow-hidden bg-gray-100 flex items-center justify-center min-h-[200px]">
                          <img
                            src={`/assets/images/${image.filename}`}
                            alt={image?.title}
                            className="max-w-full max-h-64 object-contain hover:scale-105 transition-transform duration-200 cursor-pointer"
                            onClick={() => handlePreview(image)}
                            onError={(e) => {
                              e.target.src = '/placeholder-image.png'; // Fallback if image fails to load
                              e.target.alt = 'Image failed to load';
                            }}
                          />
                        </div>
                        <div className="p-4">
                          <h4 className="font-semibold text-foreground mb-2">{image?.title || image?.originalname}</h4>
                          <p className="text-sm text-black mb-3">{image?.description}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-black bg-muted px-2 py-1 rounded">
                              {image?.category}
                            </span>
                            <span className="text-xs text-black">{image?.date || (image?.uploadedAt ? new Date(image.uploadedAt).toLocaleDateString() : '')}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Videos Section */}
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-foreground flex items-center space-x-2">
                    <Icon name="Video" size={24} />
                    <span>Videos</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {gallery?.filter(item => item?.type === 'video')?.map((video) => (
                      <div key={video?.id} className="bg-card rounded-lg border border-border overflow-hidden">
                        <div className="p-4 border-b border-border">
                          <h4 className="font-semibold text-foreground">{video?.title || video?.originalname}</h4>
                          <p className="text-sm text-black mt-1">{video?.description}</p>
                        </div>
                        <div className="aspect-video">
                          <video
                            src={`/assets/files/${video.filename}`}
                            className="w-full h-full border-0"
                            title={video?.title}
                            controls
                            preload="metadata"
                          />
                        </div>
                        <div className="p-4 border-t border-border">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-black bg-muted px-2 py-1 rounded">
                              {video?.category}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              iconName="Download"
                              onClick={() => handleDownload({...video, fileUrl: `/assets/files/${video.filename}`})}
                            >
                              Download
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Tenders Tab */}
            {activeTab === "tenders" && (
              <div className="space-y-6">
                <div className="bg-card rounded-lg border border-border p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-foreground flex items-center space-x-2">
                      <Icon name="Briefcase" size={24} />
                      <span>Open Tenders</span>
                    </h2>
                  </div>
                  <p className="text-black mb-6">
                    Stay updated with the latest tender opportunities and
                    procurement announcements from KTTI.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-6">
                  {tenders?.map((tender) => (
                    <TenderCard
                      key={tender?.id}
                      tender={tender}
                      onViewDetails={handleTenderDetails}
                    />
                  ))}
                  {tenders?.length === 0 && (
                    <div className="text-center py-12">
                      <Icon
                        name="Briefcase"
                        size={64}
                        className="mx-auto mb-4 text-black opacity-50"
                      />
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        No tenders available at the moment
                      </h3>
                      <p className="text-black">
                        Check back later for new tender opportunities.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Careers Tab */}
            {activeTab === "careers" && (
              <div className="space-y-6">
                <div className="bg-card rounded-lg border border-border p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-foreground flex items-center space-x-2">
                      <Icon name="Briefcase" size={24} />
                      <span>Career Opportunities</span>
                    </h2>
                  </div>
                  <p className="text-black mb-6">
                    Explore exciting career opportunities at KTTI. We're always looking for talented individuals to join our team.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  {careers?.map((job) => (
                    <div
                      key={job?.id}
                      className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-shadow duration-200"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-foreground mb-2">
                            {job?.title}
                          </h3>
                          <div className="flex items-center space-x-4 text-sm text-black">
                            <span className="flex items-center space-x-1">
                              <Icon name="MapPin" size={16} />
                              <span>{job?.location}</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <Icon name="Clock" size={16} />
                              <span>{job?.type}</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <Icon name="Building" size={16} />
                              <span>{job?.department}</span>
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-sm text-black">
                            Posted: {job?.postedDate}
                          </span>
                          <div className="mt-2">
                            <span className="inline-block bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
                              Deadline: {job?.applicationDeadline}
                            </span>
                          </div>
                        </div>
                      </div>

                      <p className="text-black mb-4">{job?.description}</p>

                      <div className="mb-4">
                        <h4 className="font-semibold text-foreground mb-2">Requirements:</h4>
                        <ul className="list-disc list-inside text-sm text-black space-y-1">
                          {Array.isArray(job?.requirements) ? job?.requirements?.map((req, index) => (
                            <li key={index}>{req}</li>
                          )) : <li>{job?.requirements}</li>}
                        </ul>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="text-sm text-black">
                          <span className="font-semibold">Salary:</span> {job?.salary}
                        </div>
                        <div className="flex space-x-2">
                          {job.filename && (
                            <a
                              href={`/assets/files/${job.filename}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button
                                variant="outline"
                                size="sm"
                                iconName="Download"
                                iconPosition="left"
                              >
                                Download JD
                              </Button>
                            </a>
                          )}
                          <Button
                            variant="default"
                            size="sm"
                            iconName="Mail"
                            onClick={() => window.location.href = `mailto:${job?.contactEmail}?subject=Application for ${job?.title}`}
                          >
                            Apply Now
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {careers?.length === 0 && (
                    <div className="text-center py-12">
                      <Icon
                        name="Briefcase"
                        size={64}
                        className="mx-auto mb-4 text-black opacity-50"
                      />
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        No job openings at the moment
                      </h3>
                      <p className="text-black">
                        Check back later for new career opportunities.
                      </p>
                    </div>
                  )}
                </div>


              </div>
            )}

            {/* Calendar Tab */}
            {activeTab === "calendar" && (
              <div className="max-w-4xl mx-auto">
                {calendarLoading ? (
                  <div className="text-center py-12">
                    <Icon name="Loader" size={48} className="mx-auto mb-4 text-muted-foreground animate-spin" />
                    <p className="text-muted-foreground">Loading calendar events...</p>
                  </div>
                ) : (
                  <AcademicCalendar events={academicEvents} />
                )}
              </div>
            )}
          </div>
        </section>

        {/* Filter Modal */}
        <FilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          filters={appliedFilters}
          onApplyFilters={setAppliedFilters}
        />

        {/* Preview Modal */}
        <PreviewModal
          isOpen={isPreviewModalOpen}
          onClose={handleClosePreview}
          resource={selectedResource}
        />

        {/* Tender Details Modal */}
        <TenderDetailsModal
          isOpen={isTenderDetailsModalOpen}
          onClose={handleCloseTenderDetails}
          tender={selectedTender}
        />

        {/* Footer */}
        <footer
          className="bg-card bg-blue-200 border-t border-border py-12"
          // style={{ backgroundColor: "#ffff00 " }}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="font-semibold text-black mb-4">Need Help?</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2 text-black">
                    <Icon name="Phone" size={16} />
                    <span>+254(0)705636443</span>
                  </div>
                  <div className="flex items-center space-x-2 text-black">
                    <Icon name="Mail" size={16} />
                    <span>info@karumotti.ac.ke</span>
                  </div>
                  <div className="flex items-center space-x-2 text-black">
                    <Icon name="Clock" size={16} />
                    <span>Mon-Fri: 8:00 AM - 5:00 PM</span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-black mb-4">Quick Links</h3>
                <div className="space-y-2">
                  <a
                    href="https://tveta.go.ke/"
                    target="_blank"
                    className="block text-black hover:text-primary transition-colors duration-200"
                  >
                    TVETA
                  </a>
                  <a
                    href="https://kuccps.net/"
                    target="_blank"
                    className="block text-black hover:text-primary transition-colors duration-200"
                  >
                    KUCCPS
                  </a>
                  <a
                    href="https://tvetcdacc.go.ke"
                    target='_blank'
                    className="block text-black hover:text-primary transition-colors duration-200"
                  >
                    TVET CDACC
                  </a>
                  <a
                    href="https://helb.co.ke"
                    target="_blank"
                    className="block text-black hover:text-primary transition-colors duration-200"
                  >
                    HELB
                  </a>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-4">
                  Reach out
                </h3>
                <p className="text-black mb-4">
                  Reach out to get more information about the resources and
                  updates.
                </p>
                <Button variant="default" iconName="Bell" iconPosition="left">
                  Reach Out Now
                </Button>
              </div>
            </div>
            <div className="border-t border-border mt-8 pt-8 text-center text-black">
              <p>
                &copy; {new Date()?.getFullYear()} KTTI. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Resources;
